<template>
  <div class="port-config-page">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="left">
        <span class="title">端口映射管理</span>
        <span class="refresh-timer" v-if="timerCount">
          下次刷新: {{ timerCount }}s
        </span>
      </div>
      <div class="right">
        <el-button type="info" size="small" @click="showLogDialog" :icon="Document">
          查看日志
        </el-button>
        <el-input
          v-model="searchText"
          placeholder="搜索客户端IP、用户名..."
          :prefix-icon="Search"
          clearable
          style="width: 250px"
          size="small"
        />
        <el-button 
          @click="exportClientList" 
          :disabled="clientList.length === 0"
          size="small"
        >
          导出列表
        </el-button>
        <el-button 
          @click="refreshData" 
          :loading="dataLoading"
          size="small"
          type="primary"
          :icon="Refresh"
        >
          刷新
        </el-button>
      </div>
    </div>

    <!-- 服务器配置 -->
    <el-card class="server-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>服务器设置</span>
        </div>
      </template>

      <div class="table-responsive">
        <table class="config-table">
          <thead>
            <tr>
              <th>监听端口</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td data-label="监听端口">{{ serverConfig.listenPort }}</td>
              <td data-label="状态">
                <span class="status-tag" :class="serverConfig.switch ? 'enabled' : 'disabled'">
                  {{ serverConfig.status }}
                </span>
              </td>
              <td data-label="操作" class="action-cell">
                <div class="action-buttons">
                  <!-- 编辑按钮 - 只在关闭状态显示 -->
                  <template v-if="!serverConfig.switch">
                    <el-button 
                      type="primary" 
                      size="small" 
                      @click="showServerEditDialog"
                      :disabled="dataLoading"
                    >
                      编辑
                    </el-button>
                    
                    <!-- 启动按钮 - 只在关闭状态显示 -->
                    <el-button 
                      type="success" 
                      size="small" 
                      @click="startServer"
                      :loading="dataLoading"
                    >
                      启动
                    </el-button>
                  </template>
                  
                  <!-- 停止按钮 - 只在运行状态显示 -->
                  <template v-else>
                    <el-button 
                      type="danger" 
                      size="small" 
                      @click="stopServer"
                      :loading="dataLoading"
                    >
                      停止
                    </el-button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </el-card>

    <!-- 加载状态 -->
    <el-skeleton 
      v-if="dataLoading && clientList.length === 0" 
      :rows="5" 
      animated 
      class="skeleton-card"
    />

    <!-- 错误提示 -->
    <el-alert
      v-else-if="error"
      :title="error"
      type="error"
      show-icon
      class="error-alert"
    >
      <template #action>
        <el-button size="small" type="danger" @click="refreshData">重试</el-button>
      </template>
    </el-alert>

    <!-- 客户端列表 -->
    <template v-else>
      <el-card 
        v-for="client in filteredClientList" 
        :key="client.key" 
        class="client-card" 
        shadow="hover"
      >
        <template #header>
          <div class="card-header">
            <div class="client-title">
              <span class="client-ip">{{ client.externalIP }}</span>
              <span class="client-tag client-os">{{ client.osType }}</span>
              <span class="client-tag client-user">{{ client.bindUsername }}</span>
            </div>
            <span class="client-info">
              {{ client.platform }} | {{ client.version }} | {{ formatTimeDetail(client.createTime) }}
            </span>
          </div>
        </template>

        <!-- 客户端映射列表 -->
        <div class="table-responsive">
          <table class="mapping-table" v-if="client.controlInfo.length > 0">
            <thead>
              <tr>
                <th>出口地址</th>
                <th>内网地址</th>
                <th>内网端口</th>
                <th>映射端口</th>
                <th>协议</th>
                <th>创建时间</th>
                <th>状态</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in client.controlInfo" :key="index">
                <td data-label="出口地址">{{ row.LocalConn }}</td>
                <td data-label="内网地址">{{ row.ConnectIP }}</td>
                <td data-label="内网端口">{{ row.ConnectPort }}</td>
                <td data-label="映射端口">{{ row.ListenPort }}</td>
                <td data-label="协议">
                  <span class="protocol-tag" :class="row.Protocol === 0 ? 'tcp' : 'udp'">
                    {{ row.Protocol === 0 ? 'TCP' : 'UDP' }}
                  </span>
                </td>
                <td data-label="创建时间">{{ formatTimeDetail(row.CreateTime) }}</td>
                <td data-label="状态">
                  <span class="status-tag enabled">已连接</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </el-card>

      <!-- 没有客户端时的提示 -->
      <el-card v-if="filteredClientList.length === 0" class="empty-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <span>客户端列表</span>
          </div>
        </template>
        <div class="empty-text">
          <el-empty description="暂无客户端连接" :image-size="100">
            <el-button type="primary" @click="refreshData" size="small">刷新</el-button>
          </el-empty>
        </div>
      </el-card>
    </template>

    <!-- 服务器配置弹窗 -->
    <el-dialog
      v-model="serverDialogVisible"
      title="编辑服务器配置"
      :width="isMobile ? '95%' : '500px'"
      :before-close="handleServerDialogClose"
      @close="resetServerForm"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form 
        ref="serverFormRef"
        :model="serverEditForm" 
        :rules="serverRules"
        :label-position="isMobile ? 'top' : 'right'"
        label-width="100px"
      >
        <el-form-item label="监听端口" prop="listenPort">
          <el-input-number 
            v-model="serverEditForm.listenPort" 
            :min="1" 
            :max="65535"
            controls-position="right"
            style="width: 100%"
            placeholder="请输入监听端口"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="serverDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="updateServer" :loading="dataLoading">确认</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      title="运行日志"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>共 {{ logTotal }} 条日志</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">自动刷新</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">(5秒)</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button 
            type="success" 
            size="small" 
            @click="downloadAllLogs" 
            :loading="logDownloading"
            :icon="Download"
          >
            下载所有日志
          </el-button>
          <el-button 
            type="primary" 
            size="small" 
            @click="refreshLogs" 
            :loading="logLoading"
            :icon="Refresh"
          >
            立即刷新
          </el-button>
          <el-button 
            type="danger" 
            size="small" 
            @click="clearLogs"
            :loading="logClearing"
          >
            清空日志
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>暂无日志记录</p>
        </div>
        <div v-else class="log-list">
          <div 
            v-for="(log, index) in logsData" 
            :key="index" 
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { API_BASE_URL } from '@/config';
import { useRouter } from 'vue-router'
import { Search, Refresh, Document, Download, InfoFilled } from '@element-plus/icons-vue'

export default {
  name: 'PortConfig',
  components: { InfoFilled },
  setup() {
    const router = useRouter()
    const isMobile = ref(false)
    
    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768
    }
    
    const dataLoading = ref(false)
    const error = ref(null)
    const timerCount = ref(0)
    const timer = ref(null)
    const logRefreshInterval = ref(null)
    const searchText = ref('')
    const serverFormRef = ref(null)
    
    // 日志对话框相关
    const logDialogVisible = ref(false)
    const logLoading = ref(false)
    const logClearing = ref(false)
    const logDownloading = ref(false)
    const logsData = ref([])
    const logTotal = ref(0)
    const logCurrentPage = ref(1)
    const logPageSize = ref(50)
    const logContentRef = ref(null)
    const autoRefreshEnabled = ref(true) // 默认开启自动刷新
    
    // 弹窗控制
    const serverDialogVisible = ref(false)
    
    // 服务器配置数据
    const serverConfig = reactive({
      listenPort: 0,
      status: "",
      switch: false
    })
    
    // 服务器编辑表单
    const serverEditForm = reactive({
      listenPort: 0
    })
    
    // 表单验证规则
    const serverRules = {
      listenPort: [
        { required: true, message: '请输入监听端口', trigger: 'blur' },
        { type: 'number', min: 1, max: 65535, message: '端口取值范围是1-65535', trigger: 'blur' }
      ]
    }
    
    // 客户端列表
    const clientList = ref([])
    
    // 格式化后的客户端列表
    const formattedClientList = computed(() => {
      return clientList.value.map(client => ({
        ...client,
        formattedCreateTime: formatTimeDetail(client.createTime),
        displayName: `${client.externalIP} | ${client.osType} | ${client.bindUsername}`,
        controlInfoList: client.controlInfo || []
      }))
    })
    
    // 过滤后的客户端列表
    const filteredClientList = computed(() => {
      if (!searchText.value) return formattedClientList.value;
      
      const searchLower = searchText.value.toLowerCase();
      return formattedClientList.value.filter(client => 
        (client.externalIP && client.externalIP.toLowerCase().includes(searchLower)) ||
        (client.bindUsername && client.bindUsername.toLowerCase().includes(searchLower)) ||
        (client.osType && client.osType.toLowerCase().includes(searchLower))
      );
    })
    
    // 启动定时器
    const startTimer = () => {
      if (timer.value) return;
      
      timerCount.value = 10;
      timer.value = setInterval(() => {
        timerCount.value--;
        if (timerCount.value <= 0) {
          refreshData();
          timerCount.value = 10;
        }
      }, 1000);
    }
    
    // 停止定时器
    const stopTimer = () => {
      if (timer.value) {
        clearInterval(timer.value);
        timer.value = null;
      }
    }
    
    // 刷新数据
    const refreshData = async () => {
      if (dataLoading.value) return;
      
      dataLoading.value = true;
      error.value = null;
      
      try {
        const response = await fetch(`${API_BASE_URL}/local-tunnel-server`, {
          method: 'GET',
          credentials: 'include'
        })
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = await response.json()

        if (data.errCode !== 0) {
          if (data.errCode === 1000) {
            ElMessage.warning('请先登录');
            router.push('/user-login?callback=official-server');
            return;
          } 
          throw new Error(data.errMsg || '获取数据失败')
        }
        
        // 更新服务器配置
        Object.assign(serverConfig, {
          listenPort: data.listenPort,
          status: data.status,
          switch: data.switch
        })
        
        // 处理客户端数据
        clientList.value = []
        if (data.data && data.data.ControlPool) {
          Object.entries(data.data.ControlPool).forEach(([clientKey, clientData]) => {
            clientList.value.push({
              key: clientKey,
              token: clientData.ControlToken,
              localconn: clientData.LocalConn,
              bindUsername: clientData.BindUsername,
              createTime: clientData.CreateTime,
              osType: clientData.OSType,
              externalIP: clientData.ExternalIP,
              platform: clientData.Platform,
              version: clientData.Version,
              controlInfo: clientData.ControlInfo || [],
              arch: clientData.ARCH,
              kernelArch: clientData.KernelArch
            })
          })
        }
        
      } catch (err) {
        console.error('刷新数据失败:', err);
        error.value = err.message;
        ElMessage.error('刷新失败: ' + err.message);
      } finally {
        dataLoading.value = false;
      }
    }
    
    // 显示服务器编辑弹窗
    const showServerEditDialog = () => {
      Object.assign(serverEditForm, {
        listenPort: serverConfig.listenPort || 8080
      })
      serverDialogVisible.value = true
    }
    
    // 重置服务器表单
    const resetServerForm = () => {
      if (serverFormRef.value) {
        serverFormRef.value.clearValidate()
      }
    }
    
    // 处理服务器弹窗关闭
    const handleServerDialogClose = (done) => {
      resetServerForm()
      done()
    }
    
    // 更新服务器配置
    const updateServer = async () => {
      if (!serverFormRef.value) return
      
      await serverFormRef.value.validate(async (valid) => {
        if (!valid) return
        
        dataLoading.value = true
        try {
          const formData = new URLSearchParams();
          formData.append('officialPort', serverEditForm.listenPort);

          const response = await fetch(`${API_BASE_URL}/update-local-tunnel-server`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString()
          })
          
          const data = await response.json()
          
          if (data.errCode !== 0) {
            throw new Error(data.errMsg || '更新服务器配置失败')
          }
          
          serverDialogVisible.value = false
          ElMessage.success('服务器配置已更新')
          refreshData()
        } catch (error) {
          ElMessage.error('更新失败: ' + error.message)
        } finally {
          dataLoading.value = false
        }
      })
    }

    // 启动服务器
    const startServer = async () => {
      if (!serverConfig.listenPort || serverConfig.listenPort < 1 || serverConfig.listenPort > 65535) {
        ElMessage.warning('服务器端口取值范围是1-65535')
        return
      }
      
      try {
        await ElMessageBox.confirm('确定要启动服务器吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        });
      } catch (error) {
        return;
      }
      
      dataLoading.value = true
      try {
        const formData = new URLSearchParams();
        formData.append('officialPort', serverConfig.listenPort);

        const response = await fetch(`${API_BASE_URL}/update-local-tunnel-server`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString()
        })
        
        const data = await response.json()
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '启动失败')
        }
        
        ElMessage.success('服务器已启动')
        refreshData()
      } catch (error) {
        ElMessage.error('启动失败: ' + error.message)
      } finally {
        dataLoading.value = false
      }
    }

    // 格式化时间
    const formatTimeDetail = (timestamp) => {
      if (!timestamp) return '-';
      const date = new Date(timestamp * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };
    
    // 停止服务器
    const stopServer = async () => {
      try {
        await ElMessageBox.confirm(
          '确定要停止服务器吗？停止后所有客户端连接将断开。', 
          '警告', 
          {
            confirmButtonText: '确认',
            cancelButtonText: '取消',
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
          }
        );
      } catch (error) {
        return;
      }
      
      dataLoading.value = true
      try {
        const response = await fetch(`${API_BASE_URL}/stop-local-tunnel-server`, {
          method: 'POST',
          credentials: 'include'
        })
        
        const data = await response.json()
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '停止服务器失败')
        }
        
        ElMessage.success('服务器已停止')
        refreshData()
      } catch (error) {
        ElMessage.error('停止失败: ' + error.message)
      } finally {
        dataLoading.value = false
      }
    }

    // 导出客户端列表为CSV
    const exportClientList = () => {
      if (clientList.value.length === 0) {
        ElMessage.warning('没有可导出的数据');
        return;
      }

      const data = clientList.value.map(client => ({
        'IP地址': client.externalIP || '-',
        '用户名': client.bindUsername || '-',
        '操作系统': client.osType || '-',
        '平台': client.platform || '-',
        '版本': client.version || '-',
        '连接时间': formatTimeDetail(client.createTime),
        '映射数量': client.controlInfo?.length || 0,
        '架构': client.arch || '-',
        '内核架构': client.kernelArch || '-'
      }));
      
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');
      
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `clients_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      ElMessage.success('导出成功');
    }
    
    // 显示日志对话框
    const showLogDialog = () => {
      logDialogVisible.value = true
      fetchLogs()
      // 开启日志自动刷新（如果开启状态）
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh()
      }
    }

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh()
        ElMessage.success('已开启自动刷新（5秒/次）')
      } else {
        stopLogAutoRefresh()
        ElMessage.info('已关闭自动刷新')
      }
    }

    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh() // 先停止已有的定时器
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false) // 静默刷新，不显示加载状态
        }
      }, 5000)
    }

    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value)
        logRefreshInterval.value = null
      }
    }

    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true
        }
        
        const params = new URLSearchParams({
          module: 'controlserver',
          page: logCurrentPage.value,
          size: logPageSize.value
        })
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.warning('请先登录')
          router.push('/user-login?callback=official-server')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取日志失败')
        }
        
        logsData.value = data.data || []
        logTotal.value = data.count || 0
        
        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0
        }
      } catch (error) {
        console.error('获取日志失败:', error)
        if (showLoading) {
          ElMessage.error(`获取日志失败: ${error.message || '未知错误'}`)
        }
      } finally {
        if (showLoading) {
          logLoading.value = false
        }
      }
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true)
    }

    // 清空日志
    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm('确定要清空所有端口映射服务日志吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        logClearing.value = true
        
        const params = new URLSearchParams({
          module: 'controlserver'
        })
        
        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.warning('请先登录')
          router.push('/user-login?callback=official-server')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '清空日志失败')
        }
        
        ElMessage.success('日志已清空')
        fetchLogs(true)
      } catch (error) {
        if (error !== 'cancel') {
          console.error('清空日志失败:', error)
          ElMessage.error(`清空日志失败: ${error.message || '未知错误'}`)
        }
      } finally {
        logClearing.value = false
      }
    }

    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true
        
        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'tunnel-server',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        })
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.warning('请先登录')
          router.push('/user-login?callback=official-server')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取日志失败')
        }
        
        const allLogs = data.data || []
        
        if (allLogs.length === 0) {
          ElMessage.warning('暂无日志可下载')
          return
        }
        
        // 格式化所有日志内容
        let logContent = ''
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
        })
        
        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `tunnel_server_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        ElMessage.success(`所有日志下载成功，共 ${allLogs.length} 条记录`)
      } catch (error) {
        console.error('下载日志失败:', error)
        ElMessage.error(`下载日志失败: ${error.message || '未知错误'}`)
      } finally {
        logDownloading.value = false
      }
    }

    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size
      logCurrentPage.value = 1
      fetchLogs(true)
    }

    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page
      fetchLogs(true)
    }
    
    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile()
    }
    
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', handleResize)
      refreshData()
      startTimer()
    })
    
    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
      stopTimer()
      stopLogAutoRefresh()
    })
    
    return {
      Search,
      Refresh,
      Document,
      Download,
      InfoFilled,
      isMobile,
      dataLoading,
      error,
      serverConfig,
      clientList,
      filteredClientList,
      timerCount,
      searchText,
      serverDialogVisible,
      serverEditForm,
      serverFormRef,
      serverRules,
      showServerEditDialog,
      handleServerDialogClose,
      resetServerForm,
      refreshData,
      updateServer,
      startServer,
      stopServer,
      exportClientList,
      formatTimeDetail,
      // 日志相关
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshEnabled,
      showLogDialog,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange
    }
  }
}
</script>

<style scoped>
.port-config-page {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  background-color: #f5f7fa;
  min-height: 100vh;
}

@media (max-width: 768px) {
  .port-config-page {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .port-config-page {
    padding: 8px;
  }
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    padding: 12px;
  }
}

.left {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .left {
    justify-content: space-between;
  }
}

.right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .right {
    width: 100%;
    justify-content: flex-end;
  }
  
  .right .el-input {
    width: 100% !important;
    margin-bottom: 8px;
  }
  
  .right .el-button {
    flex: 1;
  }
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.refresh-timer {
  font-size: 14px;
  color: #909399;
  background-color: #f0f2f5;
  padding: 4px 12px;
  border-radius: 16px;
}

.server-card, .client-card {
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .server-card, .client-card {
    margin-bottom: 16px;
  }
}

.skeleton-card {
  margin-bottom: 20px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
}

.error-alert {
  margin-bottom: 20px;
  border-radius: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
}

@media (max-width: 768px) {
  .table-responsive {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.config-table,
.mapping-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.config-table th,
.config-table td,
.mapping-table th,
.mapping-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ebeef5;
}

.config-table th,
.mapping-table th {
  background-color: #f5f7fa;
  color: #909399;
  font-weight: 600;
  white-space: nowrap;
}

.config-table td,
.mapping-table td {
  color: #606266;
  vertical-align: middle;
}

.config-table tr:hover td,
.mapping-table tr:hover td {
  background-color: #f5f7fa;
}

@media (max-width: 768px) {
  .config-table,
  .mapping-table {
    width: 100%;
    min-width: 100%;
    display: block;
  }
  
  .config-table thead,
  .mapping-table thead {
    display: none;
  }
  
  .config-table tbody,
  .config-table tr,
  .mapping-table tbody,
  .mapping-table tr {
    width: 100%;
    display: block;
  }
  
  .config-table tr,
  .mapping-table tr {
    margin-bottom: 16px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fff;
  }
  
  .config-table td,
  .mapping-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid #ebeef5;
    width: 100%;
    box-sizing: border-box;
  }
  
  .config-table td:before,
  .mapping-table td:before {
    content: attr(data-label);
    font-weight: 600;
    color: #909399;
    margin-right: 12px;
    flex-shrink: 0;
    min-width: 80px;
    font-size: 13px;
  }
  
  .config-table td:last-child,
  .mapping-table td:last-child {
    border-bottom: none;
  }
  
  .config-table td.action-cell,
  .mapping-table td.action-cell {
    padding: 10px 12px;
  }
  
  .config-table td.action-cell:before,
  .mapping-table td.action-cell:before {
    align-self: flex-start;
    padding-top: 4px;
  }
}

@media (max-width: 480px) {
  .config-table td,
  .mapping-table td {
    padding: 8px 10px;
    font-size: 12px;
  }
  
  .config-table td:before,
  .mapping-table td:before {
    min-width: 70px;
    font-size: 12px;
  }
}

.client-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.client-ip {
  font-weight: 600;
  color: #409EFF;
}

.client-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.client-os {
  background-color: #ecf5ff;
  color: #409eff;
}

.client-user {
  background-color: #f0f9eb;
  color: #67c23a;
}

.client-info {
  font-size: 12px;
  color: #909399;
  background-color: #f5f7fa;
  padding: 4px 10px;
  border-radius: 16px;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .client-info {
    white-space: normal;
    width: 100%;
  }
}

.status-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-tag.enabled {
  background-color: #f0f9eb;
  color: #67c23a;
}

.status-tag.disabled {
  background-color: #f5f5f5;
  color: #909399;
}

.protocol-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.protocol-tag.tcp {
  background-color: #ecf5ff;
  color: #409eff;
}

.protocol-tag.udp {
  background-color: #fdf6ec;
  color: #e6a23c;
}

.action-cell .action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
}

.action-cell .action-buttons .el-button {
  flex-shrink: 0;
}

.action-cell .action-buttons .el-button + .el-button {
  margin-left: 0;
}

@media (max-width: 768px) {
  .action-cell .action-buttons {
    justify-content: flex-end;
  }
  
  .action-cell .el-button--small {
    padding: 4px 8px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .action-cell .action-buttons {
    gap: 2px;
  }
  
  .action-cell .el-button--small {
    padding: 4px 6px;
    font-size: 11px;
  }
}

/* 日志对话框样式 */
.log-dialog :deep(.el-dialog__body) {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.log-toolbar .log-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: #606266;
}

.log-toolbar .log-stats .auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 16px;
  border-left: 1px solid #dcdfe6;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-label {
  font-size: 12px;
  color: #909399;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-interval {
  font-size: 12px;
  color: #67c23a;
}

.log-toolbar .log-actions {
  display: flex;
  gap: 8px;
}

@media (max-width: 768px) {
  .log-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .log-toolbar .log-stats {
    width: 100%;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .log-toolbar .log-stats .auto-refresh-control {
    padding-left: 0;
    border-left: none;
  }
  
  .log-toolbar .log-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .log-toolbar .log-stats {
    font-size: 12px;
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.log-content .empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
}

.log-content .empty-log .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.log-content .empty-log p {
  margin: 0;
  font-size: 14px;
}

.log-content .log-list {
  padding: 12px;
}

.log-content .log-list .log-item {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.log-content .log-list .log-item:last-child {
  border-bottom: none;
}

.log-content .log-list .log-item.log-level-error {
  background-color: #fef0f0;
}

.log-content .log-list .log-item.log-level-warn {
  background-color: #fdf6ec;
}

.log-content .log-list .log-item .log-time {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level {
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level.level-error {
  color: #f56c6c;
}

.log-content .log-list .log-item .log-level.level-warn {
  color: #e6a23c;
}

.log-content .log-list .log-item .log-level.level-info {
  color: #409eff;
}

.log-content .log-list .log-item .log-message {
  color: #303133;
  word-break: break-all;
  flex: 1;
}

@media (max-width: 768px) {
  .log-content {
    max-height: 350px;
  }
  
  .log-content .log-list {
    padding: 8px;
  }
  
  .log-content .log-list .log-item {
    font-size: 12px;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .log-content .log-list .log-item .log-time {
    width: 100%;
    margin-bottom: 2px;
  }
  
  .log-content .log-list .log-item .log-message {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .log-content {
    max-height: 300px;
  }
  
  .log-content .log-list .log-item {
    font-size: 11px;
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .log-pagination {
    justify-content: center;
  }
}

.no-mapping {
  padding: 20px 0;
  background-color: #fafafa;
}

.empty-card {
  text-align: center;
}

.empty-text {
  padding: 20px 0;
  color: #909399;
}

:deep(.el-dialog__body) {
  padding-top: 20px;
  padding-bottom: 10px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 480px) {
  .dialog-footer {
    flex-direction: column;
  }
  
  .dialog-footer .el-button {
    width: 100%;
    margin-left: 0 !important;
  }
}

/* Element Plus 对话框移动端适配 */
.settings-dialog :deep(.el-dialog) {
  margin: 0 auto;
}

@media (max-width: 768px) {
  .settings-dialog :deep(.el-dialog) {
    width: 95% !important;
    margin-top: 5vh !important;
  }
}

@media (max-width: 480px) {
  .settings-dialog :deep(.el-dialog) {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    max-height: 100%;
    border-radius: 0;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin-bottom: 0 !important;
  }
  
  .settings-dialog :deep(.el-dialog__header) {
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
  }
  
  .settings-dialog :deep(.el-dialog__body) {
    padding: 16px;
    max-height: calc(80vh - 120px);
    overflow-y: auto;
  }
  
  .settings-dialog :deep(.el-dialog__footer) {
    padding: 12px 16px;
    border-top: 1px solid #ebeef5;
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  width: 100%;
}

@media (max-width: 768px) {
  :deep(.el-input__inner) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  :deep(.el-input-number) {
    width: 100% !important;
  }
  
  :deep(.el-input-number .el-input__inner) {
    text-align: left;
  }
}

@media (max-width: 480px) {
  :deep(.el-input__inner) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

@media (max-width: 768px) {
  :deep(.el-form-item__label) {
    font-size: 13px;
    padding-bottom: 4px;
  }
}

@media (max-width: 480px) {
  :deep(.el-form-item__label) {
    font-size: 12px;
  }
}

:deep(.el-button--small) {
  padding: 6px 12px;
}

@media (max-width: 768px) {
  :deep(.el-button--small) {
    padding: 6px 10px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  :deep(.el-button--small) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>